// Global React error boundary — last line of defence against an uncaught
// render error nuking the whole tab. Production hardening; no redesign.
//
// • Catches errors thrown during render / lifecycle / constructors of any
//   descendant component.
// • Shows a minimal fallback UI: "Something went wrong" + Reload button.
// • Logs the error + componentStack to the console so platform tooling can
//   pick it up. No external sink — keeps things simple.
import React from 'react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console — picked up by browser devtools + any platform-
    // attached error reporters. Intentionally no external network call.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] uncaught render error:', error, info?.componentStack);
  }

  handleReload = () => {
    // Hard reload — wipes any half-broken React tree state. Cheaper than
    // trying to recover in-place.
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#0b1020',
          color: '#e5e7eb',
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #6366f1, #a855f7)',
              marginBottom: 20,
              fontSize: 28,
            }}
            aria-hidden="true"
          >
            !
          </div>
          <h1 style={{ fontSize: 22, margin: '0 0 8px', fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, margin: '0 0 20px', opacity: 0.75, lineHeight: 1.5 }}>
            The page hit an unexpected error. Reloading usually fixes it.
            If the problem keeps happening, contact support.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
            style={{
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              padding: '10px 22px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
